import { reactive } from 'vue'
import type { Router } from 'vue-router'
import type { MainService, SpaceData } from './main'
import { getRegisteredSpaceTools, type RegisteredSpaceTool } from '@/router/space-tools'

interface SpaceContextState {
	spaces: SpaceData[]
	currentSpace: SpaceData | null
	isLoadingSpaces: boolean
	isLoadingCurrentSpace: boolean
}

export class SpaceContextService {

	private readonly mainService: MainService
	public readonly toolItems: RegisteredSpaceTool[]

	public readonly state = reactive<SpaceContextState>({
		spaces: [],
		currentSpace: null,
		isLoadingSpaces: false,
		isLoadingCurrentSpace: false
	})

	constructor(mainService: MainService)
	{
		this.mainService = mainService
		this.toolItems = getRegisteredSpaceTools()
	}

	async ensureSpacesLoaded(force = false): Promise<SpaceData[]>
	{
		if (!force && this.state.spaces.length > 0)
			return this.state.spaces

		this.state.isLoadingSpaces = true

		try {
			const result = await this.mainService.getSpaces()

			if (result.success && Array.isArray(result.data)) {
				this.state.spaces = result.data
			}

			return this.state.spaces
		} finally {
			this.state.isLoadingSpaces = false
		}
	}

	async syncCurrentSpace(spaceIdValue: string | number | null | undefined): Promise<SpaceData | null>
	{
		if (!spaceIdValue) {
			this.clearCurrentSpace()
			return null
		}

		const numericSpaceId = Number(spaceIdValue)
		if (!Number.isFinite(numericSpaceId) || numericSpaceId < 1) {
			this.clearCurrentSpace()
			return null
		}

		this.state.isLoadingCurrentSpace = true

		try {
			await this.ensureSpacesLoaded()

			const fromList = this.state.spaces.find(space => Number(space.id) === numericSpaceId)
			if (fromList) {
				this.state.currentSpace = fromList
				localStorage.setItem('currentSpaceId', String(numericSpaceId))
				return fromList
			}

			const result = await this.mainService.getSpace(numericSpaceId)
			if (result.success && result.data) {
				const loadedSpace = result.data as SpaceData
				this.state.currentSpace = loadedSpace

				if (!this.state.spaces.some(space => Number(space.id) === numericSpaceId))
					this.state.spaces = [...this.state.spaces, loadedSpace].sort((a, b) => Number(a.id) - Number(b.id))

				localStorage.setItem('currentSpaceId', String(numericSpaceId))
				return loadedSpace
			}

			this.clearCurrentSpace()
			return null
		} finally {
			this.state.isLoadingCurrentSpace = false
		}
	}

	clearCurrentSpace(): void
	{
		this.state.currentSpace = null
		localStorage.removeItem('currentSpaceId')
	}

	async openSpace(router: Router, spaceId: string | number): Promise<void>
	{
		const normalizedId = String(spaceId)
		localStorage.setItem('currentSpaceId', normalizedId)
		await router.push(`/${normalizedId}/main/s/dashboard`)
	}

	async openSpacesSettings(router: Router): Promise<void>
	{
		this.clearCurrentSpace()
		await router.push('/main/main/s/spaces_list')
	}
}
